import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input
} from '@angular/core';

import View from 'ol/View';

import {
  AnyLayerOptions,
  Layer,
  LayerOptions,
  LayersLink,
  LayersLinkProperties
} from '../../layer/shared';
import { LayerService } from '../../layer/shared/layer.service';
import { isLayerItem } from '../../layer/utils';
import { MapBrowserComponent } from '../map-browser/map-browser.component';
import { IgoMap } from '../shared/map';

@Component({
  selector: 'igo-mini-basemap',
  templateUrl: './mini-basemap.component.html',
  styleUrls: ['./mini-basemap.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MapBrowserComponent]
})
export class MiniBaseMapComponent implements AfterViewInit {
  private layerService = inject(LayerService);
  private appRef = inject(ApplicationRef);

  readonly map = input<IgoMap>(undefined);
  readonly disabled = input<boolean>(undefined);
  readonly title = input<string>(undefined);
  readonly display = input<boolean>(undefined);
  readonly baseLayer = input<Layer>(undefined);

  public basemap = new IgoMap({
    controls: {},
    interactions: false
  });

  constructor() {
    effect(() => {
      this.handleBaseLayerChanged(this.baseLayer());
    });
  }

  ngAfterViewInit() {
    const mainView = this.map().ol.getView();
    const basemapView = new View({
      center: mainView.getCenter(),
      zoom: mainView.getZoom(),
      projection: mainView.getProjection(),
      minZoom: mainView.getMinZoom(),
      maxZoom: mainView.getMaxZoom()
    });
    this.basemap.ol.setView(basemapView);

    mainView.on(['change:center', 'change:resolution'], () => {
      this.basemap.ol.getView().setCenter(mainView.getCenter());
      this.basemap.ol.getView().setZoom(mainView.getZoom());
    });
  }

  changeBaseLayer(baseLayer: Layer) {
    if (this.disabled()) {
      return;
    }
    this.map().layerController.selectBaseLayer(baseLayer);
    this.appRef.tick();
  }

  private handleBaseLayerChanged(baselayer: Layer) {
    this.basemap.layerController.reset();

    const options: AnyLayerOptions = Object.assign(
      Object.create(baselayer.options),
      baselayer.options,
      {
        visible: true,
        baseLayer: false
      }
    );

    const layer = this.layerService.createLayer(options);
    this.basemap.layerController.add(layer);
    this.handleLinkedBaseLayer(layer);
  }

  private handleLinkedBaseLayer(baselayer: Layer): void {
    const linkedLayers: LayersLink = baselayer.options.linkedLayers;
    if (!linkedLayers) {
      return;
    }
    const links: LayersLinkProperties[] = linkedLayers.links;
    const isParentLayer: boolean = links ? true : false;
    if (isParentLayer) {
      // search for child layers
      links.map((link: LayersLinkProperties) => {
        link.linkedIds.map((linkedId: string) => {
          const layerToApply = this.map().layerController.all.find(
            (layer) =>
              isLayerItem(layer) &&
              layer.options.linkedLayers?.linkId === linkedId
          ) as Layer;
          if (layerToApply) {
            const linkedLayerOptions: LayerOptions = Object.assign(
              Object.create(layerToApply.options),
              layerToApply.options,
              {
                zIndex: 9000,
                visible:
                  layerToApply.options.linkedLayers?.showInMiniBaseMap ?? true,
                baseLayer: false
              } as LayerOptions
            );
            this.basemap.layerController.add(
              this.layerService.createLayer(linkedLayerOptions)
            );
          }
        });
      });
    }
  }
}
